// Fixed CatalogPage.jsx with stabilized dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import './ModernCatalog.css';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, orderBy, where } from 'firebase/firestore';
import OrderConfirmation from '../pages/OrderConfirmation';
import ProductDetailModal from './ProductDetailModal.jsx';
import NetworkStatus from '../components/NetworkStatus';
import ErrorHandler from '../components/ErrorHandler';
import useApi from '../utils/useApi';
import { validateEmail, validatePhone, validateRequired } from '../utils/ValidationUtils';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import './ValidationStyles.css';
import LazyImage from './LazyImage.jsx';
import OptimizedPagination from './OptimizedPagination';
import Toast from '../components/Toast';
import { Search, ShoppingCart, X } from 'lucide-react';
import { trackAddToCart, trackPurchase, trackSearch, trackCategoryFilter } from '../utils/analytics';
import { hapticSuccess, hapticLight } from '../utils/haptic';
import brandingConfig from '../config/branding.config.js';
import businessConfig from '../config/business.config.js';
import { sampleProducts } from '../data/sampleProducts.js';

const ITEMS_PER_PAGE = 12;
const USE_SAMPLE_DATA = false; // Set to false when you want to use Firestore

// Move this function outside the component to prevent recreation on each render
function generateDisplayOrderId() {
  // Get current date
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  
  // Add a unique number using timestamp
  const uniqueNumber = Math.floor(now.getTime() / 1000) % 10000; 
  
  // Create format: XB-YYMMDD-XXXX
  return `XB-${year}${month}${day}-${uniqueNumber.toString().padStart(4, '0')}`;
}

// Define the API function outside the component to stabilize it
const fetchProductsFromFirestore = async () => {
  // Use sample data if enabled (for local testing without Firestore)
  if (USE_SAMPLE_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(sampleProducts), 500); // Simulate network delay
    });
  }

  // Use 'products' collection to match what your dashboard uses
  const productsCollection = collection(db, 'products');

  // Simpler query that doesn't require a composite index
  const productsQuery = query(
    productsCollection,
    orderBy('name') // Just sort by name for now
  );

  const querySnapshot = await getDocs(productsQuery);
  const productsData = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }))
  // Filter active products client-side using isActive field
  .filter(product => product.isActive !== false);

  return productsData;
};

function CatalogPage() {
  // Load cart from localStorage on initial render
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('twoGsCraftingCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });

  const [generalNote, setGeneralNote] = useState(() => {
    try {
      const savedNote = localStorage.getItem('twoGsCraftingNote');
      return savedNote || '';
    } catch (error) {
      return '';
    }
  });
  // Initialize current page from URL query parameter
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get('page');
    const pageNumber = pageParam ? parseInt(pageParam, 10) : 1;
    return pageNumber > 0 ? pageNumber : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Get URL parameters and navigation
  const location = useLocation();
  const navigate = useNavigate();

  // New states for modern features
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [sortOption, setSortOption] = useState('name-asc');

  // Check URL parameters for tag filtering and cart opening
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tagParam = params.get('tag');
    const cartParam = params.get('cart');

    if (tagParam) {
      // Decode the tag (in case it has special characters or spaces)
      const decodedTag = decodeURIComponent(tagParam);
      setSelectedCategory(decodedTag);

      // Scroll to products section
      setTimeout(() => {
        const productsSection = document.querySelector('.filters-section');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }

  }, [location.search]);

  // Form validation states
  const [formErrors, setFormErrors] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    cartItems: ''
  });
  
  // Product detail modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  
  // Customer information state
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  
  // Memoize the onSuccess callback to prevent it from changing on each render
  const handleProductsSuccess = useCallback((data) => {
    // Calculate pagination based on fetched products
    setFilteredProducts(data); // Initialize filtered products
    const total = Math.ceil(data.length / ITEMS_PER_PAGE);
    setTotalPages(total || 1); // Ensure at least one page
    updateDisplayedItems(1, data);
  }, []);
  
  // Use our custom API hook for products fetching
  const {
    data: allProducts = [],
    error: productsError,
    isLoading: loading,
    execute: fetchProducts,
    reset: resetProductsError
  } = useApi(fetchProductsFromFirestore, {
    onSuccess: handleProductsSuccess
  });
  
  // Function to update displayed items based on current page - memoize to stabilize
  const updateDisplayedItems = useCallback((page, products) => {
    if (!products || products.length === 0) {
      setDisplayedItems([]);
      return;
    }

    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedItems(products.slice(startIndex, endIndex));
  }, []);

  // Extract unique categories from products
  const categories = useCallback(() => {
    if (!allProducts || allProducts.length === 0) return ['All'];

    const cats = new Set(['All']);
    allProducts.forEach(product => {
      if (product.tags && Array.isArray(product.tags)) {
        product.tags.forEach(tag => cats.add(tag));
      }
    });
    return Array.from(cats);
  }, [allProducts]);

  // Track previous filter values to detect changes
  const prevFiltersRef = React.useRef({ selectedCategory, searchQuery, sortOption });

  // Filter products based on search and category
  const applyFilters = useCallback(() => {
    let filtered = [...allProducts];

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(product =>
        product.tags && product.tags.includes(selectedCategory)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query)) ||
        (product.tags && product.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Apply sorting
    switch (sortOption) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        filtered.sort((a, b) => {
          const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
          const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        filtered.sort((a, b) => {
          const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
          const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
          return priceB - priceA;
        });
        break;
      default:
        // Default to name ascending
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredProducts(filtered);

    // Update pagination
    const total = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    setTotalPages(total || 1);

    // Check if filters actually changed (not just initial load)
    const filtersChanged =
      prevFiltersRef.current.selectedCategory !== selectedCategory ||
      prevFiltersRef.current.searchQuery !== searchQuery ||
      prevFiltersRef.current.sortOption !== sortOption;

    // Update the ref with current values
    prevFiltersRef.current = { selectedCategory, searchQuery, sortOption };

    let pageToShow = currentPage;

    // Only reset to page 1 if filters changed OR current page is out of bounds
    if (filtersChanged) {
      pageToShow = 1;
      setCurrentPage(1);

      // Remove page parameter from URL when resetting to page 1
      const params = new URLSearchParams(location.search);
      if (params.has('page')) {
        params.delete('page');
        const newSearch = params.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        navigate(newUrl, { replace: true });
      }
    } else if (currentPage > total) {
      // If current page is out of bounds after filtering, go to last page
      pageToShow = total;
      setCurrentPage(total);
    }

    updateDisplayedItems(pageToShow, filtered);
  }, [allProducts, selectedCategory, searchQuery, sortOption, currentPage, updateDisplayedItems, location.search, location.pathname, navigate]);

  // Apply filters when search, category, or sort option changes
  useEffect(() => {
    if (allProducts && allProducts.length > 0) {
      applyFilters();
    }
  }, [searchQuery, selectedCategory, sortOption, allProducts, applyFilters]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('twoGsCraftingCart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Save general note to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('twoGsCraftingNote', generalNote);
    } catch (error) {
      console.error('Error saving note to localStorage:', error);
    }
  }, [generalNote]);

  // Handle product parameter from URL - open specific product modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productParam = params.get('product');

    if (productParam && allProducts && allProducts.length > 0) {
      const product = allProducts.find(p => p.id === productParam);
      if (product) {
        setSelectedProduct(product);
        setShowProductDetail(true);

        // Remove the product parameter from URL after opening
        const tagParam = params.get('tag');
        const newUrl = window.location.pathname + (tagParam ? `?tag=${tagParam}` : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, allProducts]);

  // Scroll to product when returning from product page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scrollToProductId = params.get('scrollTo');

    if (scrollToProductId && displayedItems.length > 0) {
      // Wait a bit for the DOM to be ready
      const scrollTimer = setTimeout(() => {
        // Find the product card element
        const productCard = document.querySelector(`article.product-card[data-product-id="${scrollToProductId}"]`);

        if (productCard) {
          // Scroll to the product with smooth behavior
          productCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Add a subtle highlight effect
          productCard.classList.add('highlight-product');
          setTimeout(() => {
            productCard.classList.remove('highlight-product');
          }, 2000);

          // Clean up URL - remove scrollTo parameter
          params.delete('scrollTo');
          const newSearch = params.toString();
          const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
          window.history.replaceState({}, '', newUrl);
        }
      }, 100);

      return () => clearTimeout(scrollTimer);
    }
  }, [location.search, displayedItems]);

  // Auto-scroll to products grid when user searches
  useEffect(() => {
    if (searchQuery.trim()) {
      // User has entered a search query - scroll to products section
      const productsSection = document.querySelector('.products-section');
      if (productsSection) {
        const headerHeight = 100; // Account for sticky header
        const elementPosition = productsSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [searchQuery]);

  // Memoize scroll handler to prevent recreation on every render
  const handleScroll = useCallback(() => {
    // Responsive scroll threshold: 100px on mobile, 120px on desktop
    const threshold = window.innerWidth < 768 ? 100 : 120;

    if (window.scrollY > threshold) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }

    // Show back to top button after scrolling down 400px
    if (window.scrollY > 400) {
      setShowBackToTop(true);
    } else {
      setShowBackToTop(false);
    }
  }, []);

  // Throttle function to limit scroll event frequency
  const throttle = useCallback((func, delay) => {
    let lastCall = 0;
    return function(...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return func(...args);
    };
  }, []);

  // Custom event listener for opening product modals from related products
  // Memoize to prevent recreation
  const handleOpenProductModal = useCallback((event) => {
    const productId = event.detail.productId;
    const product = allProducts.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setShowProductDetail(true);
    }
  }, [allProducts]);

  // Add scroll event listener with throttling
  useEffect(() => {
    // Check initial scroll position on mount
    handleScroll();

    // Create throttled version of scroll handler (100ms delay)
    const throttledScroll = throttle(handleScroll, 100);

    window.addEventListener('scroll', throttledScroll, { passive: true });
    window.addEventListener('openProductModal', handleOpenProductModal);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      window.removeEventListener('openProductModal', handleOpenProductModal);
    };
  }, [handleScroll, handleOpenProductModal, throttle]);
  
  // Fetch products on component mount - with empty dependency array to only run once
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Update displayed items when page changes
  useEffect(() => {
    if (filteredProducts && filteredProducts.length > 0) {
      updateDisplayedItems(currentPage, filteredProducts);
    }
  }, [currentPage, filteredProducts, updateDisplayedItems]);
  
  // Pagination controls
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      // Update URL with page parameter
      const params = new URLSearchParams(location.search);
      if (page === 1) {
        params.delete('page'); // Remove page param if on page 1
      } else {
        params.set('page', page.toString());
      }

      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      navigate(newUrl, { replace: true });

      // Scroll is handled by OptimizedPagination component
    }
  }, [totalPages, location.search, location.pathname, navigate]);
  
  // Navigate to product page - preserve page parameter and product ID for scroll-back
  const openProductDetail = useCallback((product) => {
    const params = new URLSearchParams();
    if (currentPage > 1) {
      params.set('returnPage', currentPage.toString());
    }
    params.set('scrollTo', product.id); // Store product ID for scroll-back
    const queryString = params.toString();
    navigate(`/product/${product.id}${queryString ? `?${queryString}` : ''}`);
  }, [navigate, currentPage]);
  
  // Close product detail modal
  const closeProductDetail = useCallback(() => {
    setShowProductDetail(false);
    setSelectedProduct(null);
  }, []);
  
  // Add to cart - increases quantity if item already exists, otherwise adds new item
  const addToCart = useCallback((item) => {
    // Calculate the actual price (with any discount applied)
    const actualPrice = item.discount > 0
      ? item.price * (1 - item.discount / 100)
      : item.price;

    setCart(prevCart => {
      // Check if item already exists in cart (without personalization)
      const existingItemIndex = prevCart.findIndex(
        cartItem => cartItem.productId === item.id &&
                    !cartItem.personalization &&
                    (!cartItem.customizationImages || cartItem.customizationImages.length === 0)
      );

      if (existingItemIndex !== -1) {
        // Item exists - increase quantity
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + 1
        };
        return updatedCart;
      } else {
        // Item doesn't exist or has personalization - add as new item
        const uniqueId = `${item.id}-${Date.now()}`;
        return [...prevCart, {
          ...item,
          cartItemId: uniqueId,
          productId: item.id,
          productName: item.name, // Explicitly set product name for email/invoice
          quantity: 1,
          personalization: '',
          customizationImages: [],
          price: parseFloat(actualPrice.toFixed(2))
        }];
      }
    });

    // Track add to cart event in analytics
    trackAddToCart(item, 1);

    // Haptic feedback for successful add to cart
    hapticSuccess();

    // Show toast notification
    setToastMessage(`${item.name} added to cart!`);
    setShowToast(true);

    // Clear any cart items error if it exists
    setFormErrors(prev => ({
      ...prev,
      cartItems: ''
    }));
  }, []);
  
  // Cart management functions removed - cart is now handled on a separate page
  
  // Group cart items by product ID to display in a structured way
  const groupedCartItems = cart.reduce((acc, item) => {
    if (!acc[item.productId]) {
      acc[item.productId] = {
        name: item.name,
        price: item.price,
        image: item.image,
        media: item.media, // Include media array for new products
        items: []
      };
    }

    acc[item.productId].items.push({
      cartItemId: item.cartItemId,
      quantity: item.quantity,
      personalization: item.personalization,
      customizationImages: item.customizationImages || []
    });

    return acc;
  }, {});
  
  // Calculate total items in cart
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  
  // Calculate total cost
  const totalCost = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Function to format date consistently
  function formatDate(date) {
    const year = date.getFullYear();
    let month = (1 + date.getMonth()).toString().padStart(2, '0');
    let day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle returning to shop from confirmation
  const handleReturnToShop = useCallback(() => {
    setShowConfirmation(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Generate WhatsApp message for order
  const generateWhatsAppMessage = useCallback((orderData) => {
    let message = `Hello, I'd like to order the following from ${brandingConfig.businessNameShort}:%0A\n`;
    message += `Order Number: ${orderData.displayOrderId}%0A\n`;
    
    // Add customer information
    message += `Customer: ${orderData.customerInfo.name}%0A`;
    message += `Phone: ${orderData.customerInfo.phone}%0A`;
    if (orderData.customerInfo.email) {
      message += `Email: ${orderData.customerInfo.email}%0A`;
    }
    message += `%0A`;
    
    // Create a structured message by product
    Object.entries(groupedCartItems).forEach(([productId, product], index) => {
      message += `${index + 1}. ${product.name} - R${product.price} (${product.items.length} items total):%0A`;
      
      product.items.forEach((item, itemIndex) => {
        message += `   Item ${itemIndex + 1}: Quantity: ${item.quantity}, Total: R${product.price * item.quantity}`;
        message += "%0A";
      });
      
      message += "%0A";
    });
    
    message += `Total Cost: R${orderData.totalAmount}`;
    
    if (orderData.generalNote) {
      message += `%0A\nGeneral Order Notes: ${orderData.generalNote}`;
    }
    
    message += `%0A\nOrder Reference: ${orderData.displayOrderId}`;
    
    return message;
  }, [groupedCartItems]);

  // Send order details to WhatsApp
  const sendToWhatsApp = useCallback(() => {
    if (!completedOrder) return;

    const message = generateWhatsAppMessage(completedOrder);
    window.open(`https://wa.me/${businessConfig.contact.phone}?text=${message}`, '_blank');
  }, [completedOrder, generateWhatsAppMessage]);

  // Validate customer information
  const validateCustomerInfo = useCallback(() => {
    let isValid = true;
    const newErrors = { ...formErrors };
    
    // Validate name (required)
    const nameValidation = validateRequired(customerInfo.name, 'Customer name');
    if (!nameValidation.valid) {
      newErrors.customerName = nameValidation.message;
      isValid = false;
    } else {
      newErrors.customerName = '';
    }
    
    // Validate phone (required)
    const phoneValidation = validatePhone(customerInfo.phone);
    if (!phoneValidation.valid) {
      newErrors.customerPhone = phoneValidation.message;
      isValid = false;
    } else {
      newErrors.customerPhone = '';
    }
    
    // Validate email (optional)
    if (customerInfo.email) {
      const emailValidation = validateEmail(customerInfo.email);
      if (!emailValidation.valid) {
        newErrors.customerEmail = emailValidation.message;
        isValid = false;
      } else {
        newErrors.customerEmail = '';
      }
    }
    
    setFormErrors(newErrors);
    return isValid;
  }, [customerInfo.name, customerInfo.phone, customerInfo.email, formErrors]);

  // Save order to Firestore and process with error handling
  const saveOrderAndProcess = async () => {
    // First validate the cart - make sure it has items
    if (cart.length === 0) {
      setFormErrors(prev => ({
        ...prev,
        cartItems: 'Your cart is empty. Please add items before checkout.'
      }));
      return;
    }
    
    // Validate customer information
    if (!validateCustomerInfo()) {
      return;
    }
    
    // Check for network connectivity
    if (!navigator.onLine) {
      setOrderError({
        message: 'You appear to be offline. Please check your internet connection and try again.',
        isOffline: true
      });
      return;
    }
    
    // Don't proceed if already submitting
    if (orderSubmitting) return;
    
    try {
      setOrderSubmitting(true);
      setOrderError(null);
      
      // Generate a display order ID
      const displayOrderId = generateDisplayOrderId();
      
      // Prepare the order data
      const orderItems = cart.map(item => ({
        productId: item.productId,
        productName: item.name,
        price: item.price,
        quantity: item.quantity,
        totalPrice: item.price * item.quantity
      }));
      
      const orderData = {
        items: orderItems,
        totalAmount: totalCost,
        generalNote: generalNote || '',
        date: formatDate(new Date()),
        orderDate: new Date(),
        timestamp: new Date(),
        status: 'Pending',
        customer: customerInfo.name,
        orderSource: 'website',
        customerInfo: {
          name: customerInfo.name,
          phone: customerInfo.phone,
          email: customerInfo.email || '',
          contactMethod: 'Phone/Email'
        },
        displayOrderId: displayOrderId,
        // Add notification email for the business
        notificationEmail: 'pearl@mphakane.com'
      };
      
      // Save to Firestore
      const orderRef = await addDoc(collection(db, 'orders'), orderData);

      // Store the completed order for the confirmation modal
      const completedOrderData = {
        ...orderData,
        id: orderRef.id
      };

      // Track purchase in analytics
      trackPurchase(displayOrderId, orderItems, totalCost);

      // Haptic feedback for successful order
      hapticSuccess();

      // Show order confirmation
      setCompletedOrder(completedOrderData);
      setShowConfirmation(true);

      // Reset states
      setCart([]);
      setGeneralNote('');
      setShowCustomerForm(false);
      setCustomerInfo({
        name: '',
        phone: '',
        email: ''
      });

      // Clear localStorage cart and note after successful order
      try {
        localStorage.removeItem('twoGsCraftingCart');
        localStorage.removeItem('twoGsCraftingNote');
      } catch (error) {
        console.error('Error clearing cart from localStorage:', error);
      }
      
    } catch (error) {
      console.error('Error saving order:', error);
      
      // Format user-friendly error message
      let errorMessage = 'There was an error processing your order. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'You don\'t have permission to create orders.';
      } else if (error.code === 'unavailable' || error.code === 'internal') {
        errorMessage = 'Our servers are currently unavailable. Please try again later.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cannot complete this action while offline.';
      }
      
      setOrderError({
        message: errorMessage,
        originalError: error,
        code: error.code || 'unknown'
      });
    } finally {
      setOrderSubmitting(false);
    }
  };

  // Handle order error dismissal
  const dismissOrderError = useCallback(() => {
    setOrderError(null);
  }, []);

  // Handle order retry
  const retryOrder = useCallback(() => {
    setOrderError(null);
    saveOrderAndProcess();
  }, []);

  // Generate pagination numbers
  const renderPaginationNumbers = () => {
    const pages = [];
    
    // Add first page
    pages.push(
      <button 
        key="first" 
        onClick={() => goToPage(1)} 
        className={`pagination-btn ${currentPage === 1 ? 'active' : ''}`}
      >
        1
      </button>
    );
    
    // Add ellipsis if needed
    if (currentPage > 3) {
      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
    }
    
    // Add pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i > 1 && i < totalPages) {
        pages.push(
          <button 
            key={i} 
            onClick={() => goToPage(i)} 
            className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          >
            {i}
          </button>
        );
      }
    }
    
    // Add ellipsis if needed
    if (currentPage < totalPages - 2) {
      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
    }
    
    // Add last page if there's more than one page
    if (totalPages > 1) {
      pages.push(
        <button 
          key="last" 
          onClick={() => goToPage(totalPages)} 
          className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
        >
          {totalPages}
        </button>
      );
    }
    
    return pages;
  };

  // Handle proceed to checkout
  const handleProceedToCheckout = useCallback(() => {
    // Validate cart has items
    if (cart.length === 0) {
      setFormErrors(prev => ({
        ...prev,
        cartItems: 'Your cart is empty. Please add items before checkout.'
      }));
      return;
    }

    setFormErrors(prev => ({
      ...prev,
      cartItems: ''
    }));

    setShowCustomerForm(true);
  }, [cart.length]);

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  return (
    <>
      {/* Network Status Monitor */}
      <NetworkStatus />

      <div className="catalog-container">
        {/* Modern Header */}
        <header className={`modern-header ${scrolled ? 'scrolled' : ''}`}>
          <div className="header-content">
            {/* Logo Section */}
            <div className="header-logo-section">
              <picture>
                <source srcSet="/header-logo.webp" type="image/webp" />
                <img src="/header-logo.png" alt={brandingConfig.businessNameShort} className="header-logo-img" loading="eager" />
              </picture>
              {/* <span className="header-brand-text">{brandingConfig.businessNameShort}</span> */}
            </div>

            {/* Spacer to push actions to right */}
            <div style={{ flex: 1 }}></div>

            {/* Header Actions */}
            <div className="header-actions">
              {/* Search Icon Button */}
              <button
                className={`icon-button search-toggle ${isSearchOpen ? 'active' : ''}`}
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                aria-label="Toggle search"
              >
                <Search size={20} />
              </button>

              {/* Cart Button */}
              <Link to="/cart" className="icon-button cart-button">
                <ShoppingCart size={20} />
                {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
              </Link>
            </div>
          </div>

          {/* Expandable Search Bar */}
          {isSearchOpen && (
            <div className="search-bar-expanded">
              <div className="search-bar-content">
                <Search size={20} className="search-icon" />
                <input
                  type="search"
                  className="search-input"
                  placeholder="Search products..."
                  value={searchQuery}
                  inputMode="search"
                  enterKeyHint="search"
                  onChange={(e) => {
                    const newQuery = e.target.value;
                    setSearchQuery(newQuery);
                    // Track search if user has typed at least 3 characters
                    if (newQuery.trim().length >= 3) {
                      trackSearch(newQuery.trim());
                    }
                  }}
                  autoFocus
                />
                <button
                  className="search-close-btn"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setSearchQuery('');
                  }}
                  aria-label="Close search"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}
        </header>

        {/* Professional Hero Section */}
        <HeroSection />

        {/* Filters Section */}
        <section className="filters-section">
          <div className="category-chips">
            {categories().map(category => (
              <button
                key={category}
                className={`category-chip ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(category);
                  trackCategoryFilter(category);
                }}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="filter-summary">
            <div className="filter-summary-text">
              Showing {displayedItems.length} of {filteredProducts.length} products
              {searchQuery && ` for "${searchQuery}"`}
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </div>
            <div className="sort-container">
              <label htmlFor="sort-select" className="sort-label">Sort by:</label>
              <select
                id="sort-select"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="sort-dropdown"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="price-asc">Price (Low to High)</option>
                <option value="price-desc">Price (High to Low)</option>
              </select>
            </div>
          </div>
        </section>

        {/* Products Error Handler */}
        {productsError && (
          <ErrorHandler 
            error={productsError} 
            resetError={resetProductsError}
            retryAction={() => fetchProducts()}
          />
        )}

        {/* Order Error Handler */}
        {orderError && (
          <ErrorHandler 
            error={orderError} 
            resetError={dismissOrderError}
            retryAction={retryOrder}
          />
        )}

        {/* Products Section */}
        <section className="products-section">
          {loading ? (
            <div className="products-grid">
              {/* Show 12 skeleton cards while loading */}
              {[...Array(12)].map((_, index) => (
                <div key={index} className="product-card skeleton-card">
                  <div className="product-image-wrapper skeleton skeleton-block" style={{ height: '280px' }}></div>
                  <div className="product-info">
                    <div className="skeleton skeleton-text" style={{ height: '20px', width: '80%', marginBottom: '0.75rem' }}></div>
                    <div className="product-footer">
                      <div className="skeleton skeleton-text" style={{ height: '24px', width: '60px' }}></div>
                      <div className="skeleton skeleton-button" style={{ height: '32px', width: '70px', borderRadius: '20px' }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayedItems.length > 0 ? (
            <div className="products-grid">
              {displayedItems.map((item) => (
                <article
                  key={item.id}
                  className="product-card"
                  data-product-id={item.id}
                  onClick={() => openProductDetail(item)}
                >
                  {/* Product Image */}
                  <div className="product-image-wrapper">
                    <LazyImage
                      src={
                        item.media && item.media.length > 0
                          ? item.media[0].url
                          : item.image
                      }
                      alt={item.name}
                      width="280"
                      height="280"
                      className="product-image"
                      fallbackSrc="/images/placeholder.jpg"
                    />
                    {item.discount > 0 && (
                      <div className="product-discount-badge">
                        {item.discount}% OFF
                      </div>
                    )}

                    {/* Quick View Overlay */}
                    <div className="quick-view-overlay">
                      <button
                        className="quick-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickViewProduct(item);
                          openProductDetail(item);
                        }}
                      >
                        Quick View
                      </button>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="product-info">
                    <h3 className="product-name">{item.name}</h3>
                    {item.description && (
                      <p className="product-description">{item.description}</p>
                    )}

                    <div className="product-footer">
                      <div className="product-price-container">
                        {item.discount > 0 ? (
                          <>
                            <span className="product-price-original">
                              R{parseFloat(item.price).toFixed(2)}
                            </span>
                            <span className="product-price">
                              R{(parseFloat(item.price) * (1 - item.discount / 100)).toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="product-price">
                            R{parseFloat(item.price).toFixed(2)}
                          </span>
                        )}
                      </div>

                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(item);
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <h2 className="empty-state-title">
                {searchQuery || selectedCategory !== 'All'
                  ? 'No products found'
                  : 'No products available'}
              </h2>
              <p className="empty-state-text">
                {searchQuery || selectedCategory !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Please check back later for new items!'}
              </p>
            </div>
          )}
        </section>
        
        {/* Pagination Controls */}
        {/* {totalPages > 1 && !productsError && (
          <div className="pagination">
            <button 
              className="pagination-arrow" 
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &laquo; Prev
            </button>
            
            <div className="pagination-numbers">
              {renderPaginationNumbers()}
            </div>
            
            <button 
              className="pagination-arrow" 
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next &raquo;
            </button>
          </div>
        )} */}
        {totalPages > 1 && !productsError && (
          <OptimizedPagination
            totalItems={allProducts.length}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={goToPage}
          />
        )}

        {/* Product Detail Modal */}
        <ProductDetailModal 
          isOpen={showProductDetail}
          onClose={closeProductDetail}
          product={selectedProduct}
          addToCart={addToCart}
          catalog={allProducts}
        />

        {/* Order Confirmation Modal */}
        <OrderConfirmation
          isOpen={showConfirmation}
          onClose={handleReturnToShop}
          orderDetails={completedOrder}
          handleReturnToShop={handleReturnToShop}
          sendToWhatsApp={sendToWhatsApp}
          emailSent={customerInfo.email && customerInfo.email.trim() !== ''}
        />

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            className="back-to-top-btn"
            onClick={scrollToTop}
            aria-label="Back to top"
          >
            ↑
          </button>
        )}
      </div>

      {/* Professional Footer */}
      <Footer />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        show={showToast}
        onClose={() => setShowToast(false)}
        type="cart"
        actionButton={{
          text: 'View Cart',
          onClick: () => navigate('/cart')
        }}
      />
    </>
  );
}

export default CatalogPage;
